/**
 * Main trip planner page — sidebar timeline + content editor + calendar toggle.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  List,
  AlignJustify,
  Map as MapIcon,
  Settings,
  ZoomIn,
  ZoomOut,
  FileText,
  DollarSign,
  Luggage,
  ScanSearch,
  Loader2,
  Gauge,
  LayoutGrid,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useTravelTrip, useTravelFeature, useReservationScout, useTravelAI } from '../hooks';
import { FiasAIProvider, FiasAIToggle, FiasAIPopup, useFiasAIContext } from '@/components/fias-ai';
import { toast } from '@/components/ui/use-toast';
import { TravelTimeline } from '../components/TravelTimeline';
import { TravelDayEditor } from '../components/TravelDayEditor';
import { TravelTransitEditor } from '../components/TravelTransitEditor';
import { TravelCalendarView } from '../components/TravelCalendarView';
import { TravelSummaryView } from '../components/TravelSummaryView';
import { TravelMapView } from '../components/TravelMapView';
import { TravelDossierPanel } from '../components/TravelDossierPanel';
import { TravelBudgetPanel } from '../components/TravelBudgetPanel';
import { TravelTodoPanel } from '../components/TravelTodoPanel';
import { TravelCriticPanel } from '../components/TravelCriticPanel';
import { TravelShareDialog } from '../components/TravelShareDialog';
import { TravelExportImport } from '../components/TravelExportImport';
import { formatDateRange } from '../components/TravelHelpers';
import {
  getTravelSettings,
  setTravelSetting,
  ZOOM_LEVELS,
  type ZoomLevel,
  type PinnableToolId,
} from '../components/TravelSettings';

type ViewMode = 'planner' | 'calendar' | 'list' | 'map';

/**
 * Custom collision detection: if the pointer is inside a timeline droppable,
 * prefer that. Otherwise fall back to closestCenter for sortable events.
 */
const timelineAwareCollision: CollisionDetection = (args) => {
  // First check if pointer is within any timeline droppable
  const pointerCollisions = pointerWithin(args);
  const timelineHit = pointerCollisions.find(
    (c) => typeof c.id === 'string' && c.id.startsWith('timeline-'),
  );
  if (timelineHit) return [timelineHit];

  // Otherwise use closestCenter for sortable event reordering
  return closestCenter(args);
};

/** Fias AI toggle button connected to travel AI context. */
function TravelPlannerFiasAIToggle() {
  const fiasAI = useFiasAIContext();
  return <FiasAIToggle onClick={fiasAI.togglePanel} isActive={fiasAI.isPanelOpen} />;
}

/** Standard Fias AI popup wired to the planner-page context. */
function TravelPlannerFiasAIPopupConnected() {
  const fiasAI = useFiasAIContext();
  return (
    <FiasAIPopup
      isOpen={fiasAI.isPanelOpen}
      onClose={fiasAI.closePanel}
      messages={fiasAI.messages}
      isStreaming={fiasAI.isStreaming}
      onSubmit={fiasAI.submit}
      onClear={fiasAI.reset}
      onApplyProposal={fiasAI.applyProposal}
      onRejectProposal={fiasAI.rejectProposal}
    />
  );
}

export function TravelPlannerPage() {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const {
    trip,
    items,
    events,
    attachments,
    loading,
    error,
    updateTrip,
    addDay,
    addTransit,
    updateItem,
    deleteItem,
    addEvent,
    updateEvent,
    deleteEvent,
    reorderEvents,
    moveEvent,
    duplicateEvent,
    duplicateDay,
    addItemAttachment,
    addEventAttachment,
    removeAttachment,
    importTrip,
  } = useTravelTrip(tripId);

  const mapFeature = useTravelFeature('map');
  const mapEnabled = mapFeature.enabled;
  const dossierFeature = useTravelFeature('dossier');
  const dossierEnabled = dossierFeature.enabled;
  const budgetFeature = useTravelFeature('budget');
  const budgetEnabled = budgetFeature.enabled;
  const [budgetOpen, setBudgetOpen] = useState(false);
  const packingFeature = useTravelFeature('packing_list');
  const packingEnabled = packingFeature.enabled;
  const [packingOpen, setPackingOpen] = useState(false);
  const scoutFeature = useTravelFeature('reservation_scout');
  const scoutEnabled = scoutFeature.enabled;
  const { scout: runScout, isScouting } = useReservationScout(tripId);
  const criticFeature = useTravelFeature('critic');
  const criticEnabled = criticFeature.enabled;
  const [criticOpen, setCriticOpen] = useState(false);
  const shareFeature = useTravelFeature('share_links');
  const shareEnabled = shareFeature.enabled;
  const [shareOpen, setShareOpen] = useState(false);

  const fiasAICapabilities = useTravelAI(tripId);

  const [pinnedTools, setPinnedTools] = useState<PinnableToolId[]>(
    () => getTravelSettings().pinnedTools,
  );

  const [dossierOpen, setDossierOpen] = useState(false);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [viewMode, setViewModeState] = useState<ViewMode>('planner');
  // Fold back to planner if the user disables the map while viewing it.
  const setViewMode = useCallback(
    (next: ViewMode) => {
      if (next === 'map' && !mapEnabled) {
        setViewModeState('planner');
        return;
      }
      setViewModeState(next);
    },
    [mapEnabled],
  );
  useEffect(() => {
    if (!mapEnabled && viewMode === 'map') {
      setViewModeState('planner');
    }
  }, [mapEnabled, viewMode]);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [copiedEventId, setCopiedEventId] = useState<string | null>(null);
  const [copiedDayId, setCopiedDayId] = useState<string | null>(null);

  // Zoom
  const initSettings = getTravelSettings();
  const [zoomEnabled, setZoomEnabled] = useState(initSettings.zoomEnabled);
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(initSettings.zoomLevel);

  const handleZoomIn = useCallback(() => {
    const idx = ZOOM_LEVELS.indexOf(zoomLevel);
    if (idx < ZOOM_LEVELS.length - 1) {
      const next = ZOOM_LEVELS[idx + 1];
      setZoomLevel(next);
      setTravelSetting('zoomLevel', next);
    }
  }, [zoomLevel]);

  const handleZoomOut = useCallback(() => {
    const idx = ZOOM_LEVELS.indexOf(zoomLevel);
    if (idx > 0) {
      const next = ZOOM_LEVELS[idx - 1];
      setZoomLevel(next);
      setTravelSetting('zoomLevel', next);
    }
  }, [zoomLevel]);

  // Sync zoom setting when returning from settings page
  useEffect(() => {
    const s = getTravelSettings();
    setZoomEnabled(s.zoomEnabled);
    setZoomLevel(s.zoomLevel);
    setPinnedTools(s.pinnedTools);
  }, [viewMode]);

  const runScoutAction = useCallback(async () => {
    try {
      const res = await runScout({ scope: 'trip' });
      const updatedCount = res.updated.length;
      toast({
        title:
          updatedCount > 0
            ? `Updated ${updatedCount} reservation status${updatedCount === 1 ? '' : 'es'}`
            : 'No food events to classify',
        description: res.message,
      });
    } catch (err) {
      toast({
        title: 'Reservation scout failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  }, [runScout]);

  type ToolEntry = {
    id: PinnableToolId;
    label: string;
    icon: typeof FileText;
    iconClassName?: string;
    enabled: boolean;
    disabled?: boolean;
    onSelect: () => void | Promise<void>;
  };

  const tools: ToolEntry[] = [
    {
      id: 'dossier',
      label: 'Dossier',
      icon: FileText,
      enabled: dossierEnabled,
      onSelect: () => setDossierOpen(true),
    },
    {
      id: 'budget',
      label: 'Budget',
      icon: DollarSign,
      enabled: budgetEnabled,
      onSelect: () => setBudgetOpen(true),
    },
    {
      id: 'packing',
      label: 'Packing',
      icon: Luggage,
      enabled: packingEnabled,
      onSelect: () => setPackingOpen(true),
    },
    {
      id: 'scout',
      label: 'Reservations',
      icon: isScouting ? Loader2 : ScanSearch,
      iconClassName: isScouting ? 'animate-spin' : undefined,
      enabled: scoutEnabled,
      disabled: isScouting,
      onSelect: runScoutAction,
    },
    {
      id: 'critic',
      label: 'Trip Analysis',
      icon: Gauge,
      enabled: criticEnabled,
      onSelect: () => setCriticOpen(true),
    },
  ];

  const enabledTools = tools.filter((t) => t.enabled);
  const pinnedToolsResolved = enabledTools.filter((t) => pinnedTools.includes(t.id));

  // Auto-select first item if none selected
  const effectiveActiveItemId = activeItemId ?? items[0]?.itemId ?? null;

  // =========================================================================
  // Drag-and-drop system
  //
  // Uses dnd-kit's own onDragOver to know what the cursor is over.
  // When `over` is an event → sorting active, auto-scroll on.
  // When `over` is a timeline day or null → sorting frozen, auto-scroll off.
  // No pointer tracking needed — dnd-kit handles it all.
  // =========================================================================
  const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);
  const [sortDisabled, setSortDisabled] = useState(false);
  const editorScrollRef = useRef<HTMLDivElement>(null);
  const scheduleTopRef = useRef<HTMLDivElement>(null);

  // Ref so auto-scroll callback always reads latest value
  const sortDisabledRef = useRef(false);
  const dragActiveRef = useRef(false);
  const scrollMinRef = useRef(0);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragStart = useCallback(() => {
    dragActiveRef.current = true;
    // Calculate scroll ceiling (top of schedule section)
    if (editorScrollRef.current && scheduleTopRef.current) {
      const containerRect = editorScrollRef.current.getBoundingClientRect();
      const scheduleRect = scheduleTopRef.current.getBoundingClientRect();
      scrollMinRef.current =
        editorScrollRef.current.scrollTop + (scheduleRect.top - containerRect.top);
    }
  }, []);

  const handleDragOver = useCallback((e: DragOverEvent) => {
    const overId = e.over?.id as string | undefined;

    // An event ID starts with "tre_" — that's how we know we're over the sortable list
    const isOverEvent = !!overId && overId.startsWith('tre_');

    // Update timeline day highlight
    if (overId?.startsWith('timeline-') && overId !== 'timeline-sidebar') {
      setDragOverItemId(overId.replace('timeline-', ''));
    } else {
      setDragOverItemId(null);
    }

    // Disable sorting when NOT over an event (i.e. over sidebar, timeline, or empty space)
    const shouldDisableSort = !isOverEvent;
    if (shouldDisableSort !== sortDisabledRef.current) {
      sortDisabledRef.current = shouldDisableSort;
      setSortDisabled(shouldDisableSort);
    }
  }, []);

  // Cap scroll at the schedule section top during drag
  const handleEditorScroll = useCallback(() => {
    if (!dragActiveRef.current || !editorScrollRef.current) return;
    if (editorScrollRef.current.scrollTop < scrollMinRef.current) {
      editorScrollRef.current.scrollTop = scrollMinRef.current;
    }
  }, []);

  // Disable dnd-kit auto-scroll when sort is disabled (cursor not over events)
  const autoScrollConfig = useMemo(
    () => ({
      canScroll: () => !sortDisabledRef.current,
      threshold: { x: 0, y: 0.15 },
    }),
    [],
  );

  const handleDragCancel = useCallback(() => {
    dragActiveRef.current = false;
    sortDisabledRef.current = false;
    setSortDisabled(false);
    setDragOverItemId(null);
  }, []);

  const handleDragEnd = useCallback(
    async (e: DragEndEvent) => {
      dragActiveRef.current = false;
      sortDisabledRef.current = false;
      setSortDisabled(false);
      setDragOverItemId(null);

      const { active, over } = e;
      if (!over) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      // Dropped on a timeline day → move event to that day (not the catch-all sidebar zone)
      if (overId.startsWith('timeline-') && overId !== 'timeline-sidebar') {
        const targetItemId = overId.replace('timeline-', '');
        const draggedEvent = events.find((ev) => ev.eventId === activeId);
        if (draggedEvent && draggedEvent.itemId !== targetItemId) {
          await moveEvent(activeId, targetItemId);
          setActiveItemId(targetItemId);
        }
        return;
      }

      // Dropped on another event in the same day → reorder
      if (activeId !== overId && effectiveActiveItemId) {
        const currentEvents = events.filter((ev) => ev.itemId === effectiveActiveItemId);
        const ids = currentEvents.map((ev) => ev.eventId);
        const oldIndex = ids.indexOf(activeId);
        const newIndex = ids.indexOf(overId);
        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(ids, oldIndex, newIndex);
          await reorderEvents(effectiveActiveItemId, reordered);
        }
      }
    },
    [events, effectiveActiveItemId, moveEvent, reorderEvents],
  );

  const activeItem = useMemo(
    () => items.find((i) => i.itemId === effectiveActiveItemId) ?? null,
    [items, effectiveActiveItemId],
  );

  const activeEvents = useMemo(
    () =>
      activeItem?.itemType === 'day' ? events.filter((e) => e.itemId === activeItem.itemId) : [],
    [events, activeItem],
  );

  // Ctrl+C copies the active day, Ctrl+V duplicates it (only if no event is copied)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable)
        return;

      if (e.key === 'c' && effectiveActiveItemId) {
        const item = items.find((i) => i.itemId === effectiveActiveItemId);
        if (item?.itemType === 'day') {
          setCopiedDayId(effectiveActiveItemId);
        }
      }

      if (e.key === 'v' && !copiedEventId && copiedDayId) {
        e.preventDefault();
        duplicateDay(copiedDayId).then((result) => {
          setActiveItemId(result.item.itemId);
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [effectiveActiveItemId, items, copiedEventId, copiedDayId, duplicateDay]);

  // Stats
  const dayCount = items.filter((i) => i.itemType === 'day').length;
  const totalEvents = events.length;

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 border-b border-border p-3">
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="flex-1 flex">
          <div className="w-56 border-r border-border p-3 space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex-1 p-6 space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <p className="text-muted-foreground">{error || 'Trip not found'}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/a/arche_travel')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to trips
        </Button>
      </div>
    );
  }

  const handleTitleSave = () => {
    if (titleDraft.trim() && titleDraft.trim() !== trip.title) {
      updateTrip({ title: titleDraft.trim() });
    }
    setEditingTitle(false);
  };

  return (
    <FiasAIProvider capabilities={fiasAICapabilities}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
          <div className="px-4 py-2 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Button variant="ghost" size="sm" onClick={() => navigate('/a/arche_travel')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <TravelPlannerFiasAIToggle />
              {editingTitle ? (
                <Input
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTitleSave();
                    if (e.key === 'Escape') setEditingTitle(false);
                  }}
                  className="text-lg font-bold h-auto py-0.5"
                  autoFocus
                />
              ) : (
                <button
                  className="text-lg font-bold truncate hover:underline cursor-pointer text-left"
                  onClick={() => {
                    setTitleDraft(trip.title);
                    setEditingTitle(true);
                  }}
                >
                  {trip.title}
                </button>
              )}
              {dayCount > 0 && (
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatDateRange(trip.startDate, dayCount)}
                </span>
              )}
              <span className="text-xs text-muted-foreground shrink-0">{totalEvents} events</span>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {pinnedToolsResolved.map((tool) => {
                const Icon = tool.icon;
                return (
                  <Button
                    key={tool.id}
                    variant="outline"
                    size="sm"
                    disabled={tool.disabled}
                    onClick={() => void tool.onSelect()}
                    title={tool.label}
                  >
                    <Icon className={`h-3 w-3 mr-1 ${tool.iconClassName ?? ''}`} />
                    {tool.label}
                  </Button>
                );
              })}
              {enabledTools.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" title="Trip tools">
                      <LayoutGrid className="h-3 w-3 mr-1" /> Tools
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {enabledTools.map((tool) => {
                      const Icon = tool.icon;
                      return (
                        <DropdownMenuItem
                          key={tool.id}
                          disabled={tool.disabled}
                          onSelect={() => void tool.onSelect()}
                        >
                          <Icon className={`h-4 w-4 mr-2 ${tool.iconClassName ?? ''}`} />
                          {tool.label}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <TravelExportImport
                trip={trip}
                items={items}
                events={events}
                attachments={attachments}
                onImport={importTrip}
                shareEnabled={shareEnabled}
                onOpenShare={() => setShareOpen(true)}
              />
              <div className="flex border border-border rounded-md">
                <Button
                  variant={viewMode === 'planner' ? 'default' : 'ghost'}
                  size="sm"
                  className="rounded-r-none"
                  onClick={() => setViewMode('planner')}
                >
                  <List className="h-3 w-3 mr-1" /> Planner
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                  size="sm"
                  className="rounded-none border-x border-border"
                  onClick={() => setViewMode('calendar')}
                >
                  <Calendar className="h-3 w-3 mr-1" /> Calendar
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  className={mapEnabled ? 'rounded-none border-r border-border' : 'rounded-l-none'}
                  onClick={() => setViewMode('list')}
                >
                  <AlignJustify className="h-3 w-3 mr-1" /> Summary
                </Button>
                {mapEnabled && (
                  <Button
                    variant={viewMode === 'map' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-l-none"
                    onClick={() => setViewMode('map')}
                  >
                    <MapIcon className="h-3 w-3 mr-1" /> Map
                  </Button>
                )}
              </div>
              {zoomEnabled && viewMode !== 'planner' && (
                <div className="flex items-center gap-1 border border-border rounded-md">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleZoomOut}
                    disabled={zoomLevel === ZOOM_LEVELS[0]}
                  >
                    <ZoomOut className="h-3 w-3" />
                  </Button>
                  <span className="text-xs text-muted-foreground w-9 text-center tabular-nums">
                    {zoomLevel}%
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleZoomIn}
                    disabled={zoomLevel === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
                  >
                    <ZoomIn className="h-3 w-3" />
                  </Button>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/a/arche_travel/settings')}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        {viewMode === 'calendar' ? (
          <div className="flex-1 overflow-auto p-6">
            <div
              style={{
                transform: zoomLevel !== 100 ? `scale(${zoomLevel / 100})` : undefined,
                transformOrigin: 'top left',
                width: zoomLevel !== 100 ? `${10000 / zoomLevel}%` : undefined,
              }}
            >
              <TravelCalendarView
                trip={trip}
                items={items}
                events={events}
                attachments={attachments}
                onSelectItem={(id) => {
                  setActiveItemId(id);
                }}
                onSwitchToPlanner={() => setViewMode('planner')}
              />
            </div>
          </div>
        ) : viewMode === 'list' ? (
          <div className="flex-1 overflow-auto p-6">
            <div
              style={{
                transform: zoomLevel !== 100 ? `scale(${zoomLevel / 100})` : undefined,
                transformOrigin: 'top left',
                width: zoomLevel !== 100 ? `${10000 / zoomLevel}%` : undefined,
              }}
            >
              <TravelSummaryView
                trip={trip}
                items={items}
                events={events}
                attachments={attachments}
                activeItemId={effectiveActiveItemId}
                onSelectItem={(id) => setActiveItemId(id)}
              />
            </div>
          </div>
        ) : viewMode === 'map' && mapEnabled ? (
          <div className="flex-1 overflow-hidden">
            <TravelMapView
              trip={trip}
              items={items}
              events={events}
              activeItemId={effectiveActiveItemId}
              onSelectItem={(id) => setActiveItemId(id)}
            />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={timelineAwareCollision}
            autoScroll={autoScrollConfig}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div className="flex-1 flex overflow-hidden">
              {/* Sidebar timeline */}
              <div className="w-56 border-r border-border shrink-0 overflow-hidden">
                <TravelTimeline
                  trip={trip}
                  items={items}
                  activeItemId={effectiveActiveItemId}
                  dragOverItemId={dragOverItemId}
                  onSelectItem={setActiveItemId}
                  onUpdateItem={updateItem}
                  onAddDay={async () => {
                    const result = await addDay();
                    setActiveItemId(result.item.itemId);
                  }}
                  onAddTransit={async () => {
                    const result = await addTransit();
                    setActiveItemId(result.item.itemId);
                  }}
                />
              </div>

              {/* Main editor */}
              <div
                ref={editorScrollRef}
                className="flex-1 overflow-auto p-6"
                onScroll={handleEditorScroll}
              >
                <div className="max-w-2xl mx-auto">
                  {!activeItem ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <p>Add a day or transit segment to get started.</p>
                    </div>
                  ) : activeItem.itemType === 'day' ? (
                    <TravelDayEditor
                      trip={trip}
                      item={activeItem}
                      allItems={items}
                      events={activeEvents}
                      attachments={attachments}
                      scheduleTopRef={scheduleTopRef}
                      sortDisabled={sortDisabled}
                      copiedEventId={copiedEventId}
                      onCopyEvent={setCopiedEventId}
                      onPasteEvent={async () => {
                        if (!copiedEventId) return;
                        await duplicateEvent(copiedEventId, activeItem.itemId);
                      }}
                      onUpdateItem={updateItem}
                      onDeleteItem={async (id) => {
                        await deleteItem(id);
                        setActiveItemId(null);
                      }}
                      onAddEvent={addEvent}
                      onUpdateEvent={updateEvent}
                      onDeleteEvent={deleteEvent}
                      onReorderEvents={reorderEvents}
                      onMoveEvent={moveEvent}
                      onDuplicateEvent={duplicateEvent}
                      onUpdateTrip={updateTrip}
                      onAddItemAttachment={addItemAttachment}
                      onAddEventAttachment={addEventAttachment}
                      onRemoveAttachment={removeAttachment}
                    />
                  ) : (
                    <TravelTransitEditor
                      item={activeItem}
                      onUpdateItem={updateItem}
                      onDeleteItem={async (id) => {
                        await deleteItem(id);
                        setActiveItemId(null);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </DndContext>
        )}

        {dossierEnabled && trip && (
          <TravelDossierPanel
            tripId={trip.tripId}
            open={dossierOpen}
            onOpenChange={setDossierOpen}
          />
        )}

        {budgetEnabled && trip && (
          <TravelBudgetPanel tripId={trip.tripId} open={budgetOpen} onOpenChange={setBudgetOpen} />
        )}

        {packingEnabled && trip && (
          <TravelTodoPanel tripId={trip.tripId} open={packingOpen} onOpenChange={setPackingOpen} />
        )}

        {criticEnabled && trip && (
          <TravelCriticPanel tripId={trip.tripId} open={criticOpen} onOpenChange={setCriticOpen} />
        )}

        {shareEnabled && trip && (
          <TravelShareDialog tripId={trip.tripId} open={shareOpen} onOpenChange={setShareOpen} />
        )}

        <TravelPlannerFiasAIPopupConnected />
      </div>
    </FiasAIProvider>
  );
}
